import type { PlanGoalState } from '@developerz/domain';
import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { StatusPill, type StatusTone } from './StatusPill';

// Where a goal stands (plan 101 slice 12): the domain's `planGoalState` label over the plan's
// legs, served as `goalState` on both `GET /v1/plans` rows and `GET /v1/plans/:planId`, so the
// `/fleet/plans` card and the epic view wear the same word. Presenter only.
//
// TONE: the four phases where something is moving now (`building` .. `verifying`) are LIVE
// state, the one place this pill may take the accent (design-system.md § Accent means live);
// `done` is settled success; `ended` waits on a person (a stalled leg, or the closer about to
// write it), so it reads as danger; `cancelled` was a person's choice and stays neutral.
//
// ABSENT, not `⟦key⟧`, when the field is: `usePlans` / `usePlan` read with the cast overload
// of `apiFetch` and parse nothing, so a row from an API pod that predates slice 12 carries no
// `goalState` (the roll window `PlanCard.tsx` spells out). Retire the `Show` with that window.

const GOAL_TONE: Record<PlanGoalState, StatusTone> = {
  building: 'live',
  merging: 'live',
  deploying: 'live',
  verifying: 'live',
  done: 'success',
  ended: 'danger',
  cancelled: 'neutral',
};

const GOAL_LABEL: Record<PlanGoalState, string> = {
  building: 'fleet.epic.goal.state.building',
  merging: 'fleet.epic.goal.state.merging',
  deploying: 'fleet.epic.goal.state.deploying',
  verifying: 'fleet.epic.goal.state.verifying',
  done: 'fleet.epic.goal.state.done',
  ended: 'fleet.epic.goal.state.ended',
  cancelled: 'fleet.epic.goal.state.cancelled',
};

export function PlanGoalPill(props: {
  readonly goalState: PlanGoalState | undefined;
}): JSX.Element {
  return (
    <Show when={props.goalState}>
      {(state) => (
        <StatusPill tone={GOAL_TONE[state()]}>
          {t('fleet.epic.goal.label', { state: t(GOAL_LABEL[state()]) })}
        </StatusPill>
      )}
    </Show>
  );
}
